import React, { useState, useEffect, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import {
    Box, Typography, CircularProgress, Alert, Button, Chip, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

const STATUS_TABS = [
    { label: 'Aktuelle', value: 'AKTIV' },
    { label: 'Alte/Eingelöste', value: 'EINGELOEST' },
];

const AGE_CHIPS = [
    { label: 'Heute', value: 'Heute' },
    { label: 'Letzte 7 Tage', value: '7Tage' },
    { label: 'Älter', value: 'Älter' },
];

const PinExchangeDashboard = ({ posConfig, embedded }) => {
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [pins, setPins] = useState([]);
    const [statusFilter, setStatusFilter] = useState('AKTIV');
    const [ageFilter, setAgeFilter] = useState(null);
    const [speditionFilter, setSpeditionFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nextToken, setNextToken] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const navigate = useNavigate();

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWsEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleWsEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);
            if (msg.task === 'respGetAllPinRecords') {
                if (msg.status === 'succeeded') {
                    if (msg.nextTokenUsed) {
                        // Appending paginated results
                        setPins((prev) => [...prev, ...(msg.pins || [])]);
                    } else {
                        setPins(msg.pins || []);
                    }
                    setNextToken(msg.nextToken || null);
                    setError(null);
                } else {
                    setError(msg.message || 'Fehler beim Laden der PIN-Records.');
                }
                setLoading(false);
                setLoadingMore(false);
                return;
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, []);

    const loadPins = useCallback((opts = {}) => {
        const token = opts.nextToken || null;
        if (token) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setPins([]);
        }
        setNextToken(null);
        sendMessage(JSON.stringify({
            task: 'getAllPinRecords',
            station: cookies.station,
            staff: cookies.staff,
            statusFilter: opts.statusFilter ?? statusFilter,
            speditionFilter: (opts.speditionFilter ?? speditionFilter) || undefined,
            ageFilter: (opts.ageFilter ?? ageFilter) || undefined,
            searchTerm: (opts.searchTerm ?? searchTerm) || undefined,
            nextToken: token || undefined,
        }));
    }, [sendMessage, cookies.station, cookies.staff, statusFilter, speditionFilter, ageFilter, searchTerm]);

    useEffect(() => {
        if (readyState === ReadyState.OPEN) loadPins();
    }, [readyState, statusFilter, ageFilter, speditionFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        loadPins({ searchTerm });
    };

    const handleLoadMore = () => {
        if (nextToken) loadPins({ nextToken });
    };

    // Group pins by kdnr for customer list view
    const customerMap = {};
    pins.forEach((pin) => {
        const key = String(pin.kdnr);
        if (!customerMap[key]) {
            customerMap[key] = {
                kdnr: pin.kdnr,
                customerName: pin.kundenName || '',
                count: 0,
            };
        }
        customerMap[key].count += 1;
        // Use the latest customer name if available
        if (pin.kundenName && !customerMap[key].customerName) {
            customerMap[key].customerName = pin.kundenName;
        }
    });
    const customers = Object.values(customerMap);

    const activeTabIdx = STATUS_TABS.findIndex((t) => t.value === statusFilter);

    return (
        <Box sx={{ minHeight: embedded ? 'auto' : '100vh' }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', px: embedded ? 0 : 2.5, py: embedded ? 0 : 2 }}>
                {/* Status toggle tabs */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    {!embedded && (
                        <>
                        <Button size="small" variant="outlined" disableElevation
                            sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#ddd', color: '#1a1a1a', bgcolor: '#fff' }}
                            onClick={() => navigate('search')}>
                            PIN suchen / anfordern
                        </Button>
                        <Button size="small" variant="outlined" disableElevation
                            sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#ddd', color: '#1a1a1a', bgcolor: '#fff' }}
                            onClick={() => navigate('create')}>
                            + PIN erfassen
                        </Button>
                        <Button size="small" variant="outlined" disableElevation
                            sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#ddd', color: '#1a1a1a', bgcolor: '#fff' }}
                            onClick={() => navigate('monitor')}>
                        Monitoring
                    </Button>
                    </>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {STATUS_TABS.map((tab, i) => (
                        <Button key={tab.value} size="small" disableElevation
                            variant={activeTabIdx === i ? 'contained' : 'text'}
                            sx={{
                                borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                                bgcolor: activeTabIdx === i ? '#1a1a1a' : 'transparent',
                                color: activeTabIdx === i ? '#fff' : '#777',
                                '&:hover': { bgcolor: activeTabIdx === i ? '#333' : '#e5e5e3' },
                                minWidth: 0, px: 2,
                            }}
                            onClick={() => setStatusFilter(tab.value)}>
                            {tab.label}
                        </Button>
                    ))}
                </Box>

                {/* Filter row: Age chips, Spedition, Search */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {AGE_CHIPS.map((chip) => (
                        <Chip
                            key={chip.value}
                            label={chip.label}
                            size="small"
                            clickable
                            variant={ageFilter === chip.value ? 'filled' : 'outlined'}
                            onClick={() => setAgeFilter(ageFilter === chip.value ? null : chip.value)}
                            sx={{
                                height: 24, fontSize: 11, fontWeight: 600,
                                bgcolor: ageFilter === chip.value ? '#1a1a1a' : 'transparent',
                                color: ageFilter === chip.value ? '#fff' : '#777',
                                borderColor: '#ddd',
                                '&:hover': { bgcolor: ageFilter === chip.value ? '#333' : '#e5e5e3' },
                            }}
                        />
                    ))}
                    <TextField
                        size="small"
                        placeholder="Spedition"
                        value={speditionFilter}
                        onChange={(e) => setSpeditionFilter(e.target.value)}
                        onBlur={() => loadPins()}
                        onKeyDown={(e) => { if (e.key === 'Enter') loadPins(); }}
                        sx={{ width: 140, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <form onSubmit={handleSearchSubmit} style={{ display: 'flex' }}>
                        <TextField
                            size="small"
                            placeholder="kdnr / mpsid / Sendungsnr."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 16, color: '#999' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 220, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                        />
                    </form>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : customers.length === 0 ? (
                    <Typography sx={{ py: 6, textAlign: 'center', color: '#999', fontSize: 14 }}>
                        Keine PIN-Records gefunden.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {customers.map((cust) => (
                                <Box
                                    key={cust.kdnr}
                                    onClick={() => navigate(`customer/${cust.kdnr}`)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 2,
                                        px: 2, py: 1.5, borderRadius: 2, cursor: 'pointer',
                                        bgcolor: '#fff', border: '1px solid #eee',
                                        '&:hover': { bgcolor: '#f7f7f5', borderColor: '#ddd' },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', minWidth: 70 }}>
                                        {cust.kdnr}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: '#555', flex: 1 }}>
                                        {cust.customerName || '–'}
                                    </Typography>
                                    <Chip
                                        label={`${cust.count} PIN${cust.count !== 1 ? 's' : ''}`}
                                        size="small"
                                        sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#e5e5e3' }}
                                    />
                                </Box>
                            ))}
                        </Box>

                        {nextToken && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disableElevation
                                    disabled={loadingMore}
                                    onClick={handleLoadMore}
                                    sx={{
                                        borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                                        borderColor: '#ddd', color: '#1a1a1a',
                                    }}
                                >
                                    {loadingMore ? <CircularProgress size={16} /> : 'Mehr laden'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PinExchangeDashboard;
